import { Button, Card, Tag, Tooltip } from 'antd';
import { ArrowRight, Clock, Link as LinkIcon, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AgentName } from '@/components/AgentName';
import { AccountStatus, type ChannelAccount } from '@/proto/agentplatform/v1/channel_pb';
import { formatTimestamp } from '@/utils/utils';

interface ChannelAccountCardProps {
  item: ChannelAccount;
  /** 当前 tab 的 URL slug，进入详情时带上 ?type=<slug> 以便返回落回原 tab */
  typeSlug?: string;
  onToggleDisable: (item: ChannelAccount) => void;
  onEdit: (item: ChannelAccount) => void;
  onDelete: (item: ChannelAccount) => void;
  toggling?: boolean;
}

/** 卡片视图单卡：状态 Tag + 名称 + 绑定实例 + 时间 + 操作。对齐参考页 .acc-card。 */
export default function ChannelAccountCard({
  item,
  typeSlug,
  onToggleDisable,
  onEdit,
  onDelete,
  toggling,
}: ChannelAccountCardProps) {
  const navigate = useNavigate();
  const online = item.status === AccountStatus.ONLINE;

  // 进入详情时带上当前 tab 的 slug，返回时据此落回原 tab。
  const detailHref = (id: string) =>
    typeSlug ? `/channel-accounts/${id}?type=${typeSlug}` : `/channel-accounts/${id}`;

  return (
    <Card size="small" hoverable className="acc-card" onClick={() => navigate(detailHref(item.id))}>
      <header className="acc-head">
        <Tag color={online ? 'success' : 'default'}>{online ? '在线' : '离线'}</Tag>
        <span className="acc-id pp-mono">{item.id}</span>
      </header>
      <div className="acc-name">{item.name || '—'}</div>
      {item.agentId ? (
        <Tag color="processing" bordered={false} className="acc-bind">
          <LinkIcon size={11} /> <AgentName agentId={item.agentId} />
        </Tag>
      ) : (
        <Tag bordered={false} className="acc-bind">
          <LinkIcon size={11} /> 未绑定
        </Tag>
      )}
      <div className="acc-stats">
        <div>
          <Clock size={11} />
          更新 {formatTimestamp(item.updatedAt)}
        </div>
      </div>
      <footer className="acc-foot">
        <Tooltip title={item.disabled ? '启用' : '停用'}>
          <Button
            type="text"
            size="small"
            disabled={toggling}
            icon={item.disabled ? <Play size={14} /> : <Pause size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onToggleDisable(item);
            }}
          />
        </Tooltip>
        <Tooltip title="编辑">
          <Button
            type="text"
            size="small"
            icon={<Pencil size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
          />
        </Tooltip>
        <Tooltip title="删除">
          <Button
            type="text"
            size="small"
            icon={<Trash2 size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
          />
        </Tooltip>
        <span className="goto">
          查看详情 <ArrowRight size={12} />
        </span>
      </footer>
    </Card>
  );
}
